import {
  AlertOutlined,
  CoffeeOutlined,
  InfoCircleFilled,
  SwapRightOutlined,
  SwapLeftOutlined,
  StarFilled,
} from "@ant-design/icons";
import { Typography, Badge } from "antd";
import UserAvatar from "../../../common/UserAvatar";
const {Text} = Typography;
import React from "react";

import Moment from 'moment'
import { getInitials, getAvatarColor, getAvatarRingColor, avatarInitialsColor } from '../../../../utils/avatar';
export { getInitials, getAvatarColor, getAvatarRingColor, avatarInitialsColor };


export const getActiveTime = (date) => {
  if (!date) return Moment().format('MMM D')

  const currentDay = Moment().format('D M YYYY')
  const lastConvoDay = Moment(date).format('D M YYYY')
  const minutesDiff = Moment().diff(Moment(date), 'minutes')
  const secondsDiff = Moment().diff(Moment(date), 'seconds')
  const dateYearBefore = Moment(date).isBefore(Moment(), 'year')

  // format if message was sent less than a minute
  if (secondsDiff < 60) {
    return `${secondsDiff} sec${secondsDiff > 1 ? 's' : ''} ago`
    // format if message was sent less than an hour
  } else if (minutesDiff < 60) {
    return `${minutesDiff} min${minutesDiff > 1 ? 's' : ''} ago`
    // format if message is sent within the day
  } else if (currentDay === lastConvoDay) {
    return Moment(date).format('h:mm A')
    // format if message was sent from previous years
  } else if (dateYearBefore) {
    return Moment(date).format('MMM D, YYYY h:mm A')
  } else {
    return Moment(date).format('MMM D h:mm A')
  }
}

interface TileAvatarProps {
  fullname: string;
  direction?: string;
  star?: boolean;
}

export const TileAvatar: React.FC<TileAvatarProps> = ({ fullname, direction = 'inbound', star }) => {
  const baseAvatar = direction === 'inbound' ? (
    <UserAvatar
      name={fullname}
      size="large"
      style={{ backgroundColor: "#fff", border: "1px solid #38b549", color: "#888" }}
    />
  ) : (
    <UserAvatar
      size="large"
      style={{ background: "#fff", border: "1px solid #38b549" }}
      icon={<SwapLeftOutlined style={{ color: "#888" }} />}
    />
  );

  return star ? (
    <Badge offset={[-35, 33]} style={{ color: 'gold' }} count={<StarFilled />}>
      {baseAvatar}
    </Badge>
  ) : baseAvatar;
};


export interface GHNotif {
  badge: React.JSX.Element; // React component
  title: any; // String title
  timestamp: string; // Date object
}

function removeFocusFromDiv(divId: string) {
  const div = document.getElementById(divId);

  if (!div) return;
  // Get all focusable elements inside the div
  const focusableElements = div.querySelectorAll(
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
  );

  // Loop through and blur each element to remove focus
  focusableElements.forEach((element) => (element as HTMLElement).blur());
}

export function removeFocusAll() {
  removeFocusFromDiv("goHireCExtensionPanel");
}


export function toggleSidePanel() {
  removeFocusAll();
  const sidePanel = document.getElementById("goHireCExtensionPanel");
  if (sidePanel) {
    sidePanel.shadowRoot?.getElementById("shadowroot")?.classList.toggle("active");
  }
}

export function getShadowRoot(){
  return document.getElementById("goHireCExtensionPanel")
}

/**
 * GoHire Avatar Color Palette - Soft pastel backgrounds for enterprise look
 * Uses hash of name to consistently assign same color to same person
 */
// const avatarPastelColors = [
//   { bg: '#E8F5E9', ring: '#43A047' }, // Green
//   { bg: '#E0F2F1', ring: '#00897B' }, // Teal
//   { bg: '#E3F2FD', ring: '#1E88E5' }, // Blue
//   { bg: '#E8EAF6', ring: '#5C6BC0' }, // Indigo
//   { bg: '#F3E5F5', ring: '#8E24AA' }, // Purple
//   { bg: '#FFF3E0', ring: '#FB8C00' }, // Orange
//   { bg: '#FFFDE7', ring: '#FDD835' }, // Yellow
//   { bg: '#FFEBEE', ring: '#E53935' }, // Red
// ];

// // Dark text color for initials
// export { avatarInitialsColor };

// /**
//  * Simple hash function to convert a string to a consistent number
//  */
// const hashString = (str: string): number => {
//   let hash = 0;
//   for (let i = 0; i < str.length; i++) {
//     const char = str.charCodeAt(i);
//     hash = ((hash << 5) - hash) + char;
//     hash = hash & hash; // Convert to 32bit integer
//   }
//   return Math.abs(hash);
// };

// /**
//  * Get pastel background color for avatar based on name
//  * Same person always gets same color
//  */
// export { getAvatarColor };

// /**
//  * Get matching ring/border color for avatar based on name
//  * Same person always gets same color
//  */
// export { getAvatarRingColor };

export const getRandomSentence = () => {
  const sentences = [
    "Hey, what’s up?",
    "Can we meet tomorrow?",
    "I’m running late, sorry!",
    "How was your day?",
    "Did you see the game last night?",
    "Let’s catch up soon!",
    "Thanks for the help!",
    "I’m excited for the weekend!",
    "Can you send me that file?",
    "What’s your plan for today?",
    "I’m not sure about that.",
    "Can we talk later?",
    "I’ve been thinking about you.",
    "Have you heard from Jane?",
    "I can’t wait to see you!",
    "Let’s grab coffee sometime.",
    "I’m here if you need me.",
    "Do you want to go for a walk?",
    "Sorry, I missed your call.",
    "What time should we meet?",
    "I’m so glad we talked.",
    "Let me know if you need anything.",
    "I’m almost there.",
    "Can we reschedule?",
    "I’ll be home soon.",
    "Thanks for understanding.",
    "I’m looking forward to it!",
    "What’s the latest?",
    "Let’s plan something fun.",
    "Can you pick up some groceries?",
    "I hope you’re doing well.",
    "How’s everything going?",
    "Are you free this weekend?",
    "Let’s discuss this further.",
    "I’m here if you want to talk.",
    "Can you confirm the time?",
    "I’m running out of time.",
    "I’m sorry about earlier.",
    "What did you think of the movie?",
    "I can’t stop thinking about it.",
    "I hope to see you soon.",
    "Let’s make some plans.",
    "I’ll send you the details.",
    "I really appreciate your help.",
    "What’s your favorite restaurant?",
    "Let me know your thoughts.",
    "I’m looking forward to our meeting.",
    "Thanks for the update.",
    "I’m available anytime.",
    "I’m excited for our trip.",
    "See you soon!",
    "Can you help me with this?",
  ];

  // Get a random index from the array
  const randomIndex = Math.floor(Math.random() * sentences.length);

  // Return the sentence at the random index
  return sentences[randomIndex];
};


const jsonParse = str => {
	try {
		return JSON.parse(str);
	} catch (e) {
		return str;
	}
}

export const extractMessageText = (message: any): string => {
  const parsed = jsonParse(message);
  if (Array.isArray(parsed)) {
    // bot/flow messages: ["text", "text", { quickReplies }, ...] — keep strings, drop metadata objects
    return parsed.filter((p) => typeof p === 'string').join('\n');
  }
  if (parsed && typeof parsed === 'object') {
    return parsed.text ?? parsed.message ?? '';
  }
  return parsed == null ? '' : String(parsed);
};

export const parseNewLine = (message) => {
  const items = extractMessageText(message).split('\n')
  const msgItemArr = [];
  const msgOutput = [];

  items.map((item, index) => {
    msgItemArr.push(
      <span key={index}>
        {item}
        <br />
      </span>
    );
  });

  msgOutput.push(
    <div key={0} className="">
      {msgItemArr}
    </div>
  );
  return msgOutput;
};


export const getRandomNames = () => {
  const names = [
    "Jonathan Duarte",
    "Matti Browne",
    "Michael Johnson",
    "Emily Davis",
    "Sway Cañete",
    "Sarah Wilson",
    "James Taylor",
    "Olivia Martinez",
    "GoBe",
    "Sophia Thompson",
  ];

  // Randomly determine how many names to return (between 1 and 6)
  const numberOfNames = Math.floor(Math.random() * 6) + 1;

  // Shuffle the names array
  const shuffledNames = names.sort(() => 0.5 - Math.random());

  // Return the first `numberOfNames` names
  return shuffledNames.slice(0, numberOfNames);
};

export const isValidPhoneNumberIntl = (str) => {
  return /^\+?\d{10,15}$/.test(str); // Allows optional '+' at the beginning
};

// export const getInitials = (name: string) => {

//   if(!name || !name.length){
//     return ""
//   }

//   const nameParts = name.trim().split(" ");

//   if (nameParts.length === 1) {
//     return nameParts[0].charAt(0).toUpperCase();
//   } else {
//     const firstNameInitial = nameParts[0].charAt(0).toUpperCase();
//     const lastNameInitial = nameParts[nameParts.length - 1]
//       .charAt(0)
//       .toUpperCase();
//     return firstNameInitial + lastNameInitial;
//   }
// };

export const getRandomPhoneNumber = () => {
  const getRandomDigits = (length: number) => {
    let digits = "";
    for (let i = 0; i < length; i++) {
      digits += Math.floor(Math.random() * 10);
    }
    return digits;
  };

  const areaCode = getRandomDigits(3);
  const firstPart = getRandomDigits(3);
  const secondPart = getRandomDigits(4);

  return `(${areaCode}) ${firstPart}-${secondPart}`;
};

export const getRandomNotif = () => {
  let notif: GHNotif = {
    title: "",
    badge: <InfoCircleFilled />,
    timestamp: "Yesterday, 10:30 AM",
  };
  const titles = [
    "wrote :",
    "just came in through your texting number.",
    "has entered Talk to Agent mode.",
  ];

  // Get a random index from the titles a.k.a. types array
  const randomIndex = Math.floor(Math.random() * titles.length);

  switch (randomIndex) {
    case 0:
      notif.title = (
        <>
          {titles[randomIndex]} <em>"{getRandomSentence()}"</em>
        </>
      );
      notif.badge = (
        <UserAvatar
          size="small"
          shape="circle"
          style={{ backgroundColor: "#0073e6", border: "2px solid #fff" }}
          icon={<SwapRightOutlined color="#fff" />}
        />
      );
      break;
    case 1:
      notif.title = `${titles[randomIndex]}`;
      notif.badge = (
        <UserAvatar
          size="small"
          shape="circle"
          style={{ backgroundColor: "#ffa500", border: "2px solid #fff" }}
          icon={<CoffeeOutlined color="#fff" />}
        />
      );
      break;
    case 2:
      notif.title = `${titles[randomIndex]}`;
      notif.badge = (
        <UserAvatar
          size="small"
          shape="circle"
          style={{ backgroundColor: "#ff5c5c", border: "2px solid #fff" }}
          icon={<AlertOutlined color="#fff" />}
        />
      );
  }

  // Return the sentence at the random index
  return notif;
};
